/**
 * Grand Quack Auto — duck evolution tier data + GLTF preloader.
 *
 * Extracted from duckEvolutionSystem.ts so the system file stays under the
 * project's 200-line cap (CLAUDE.md). Pure data + asset-loading concerns
 * live here; scene mutation + cinematic sequencing stays in the system.
 *
 * Models are sourced from Poly Pizza (CC-BY/CC0). Each tier defines the
 * chaos threshold required to unlock it, the GLB to swap in, and the visual
 * scale + emoji icon used by the HUD.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// ── Evolution tiers ───────────────────────────────────────────────────────

export interface DuckTier {
  name: string;
  chaosThreshold: number;
  modelPath: string | null;
  scale: number;
  /** Visual emoji icon for the evolution display. */
  icon: string;
}

export const DUCK_TIERS: DuckTier[] = [
  { name: 'Lil\' Quacker',       chaosThreshold: 0,   modelPath: null,                       scale: 0.45, icon: '🐣' },
  { name: 'Puddle Punk',         chaosThreshold: 15,  modelPath: '/models/duckling.glb',      scale: 0.6,  icon: '🐥' },
  { name: 'Sir Quacks-a-Lot',    chaosThreshold: 50,  modelPath: '/models/rubber-duck.glb',   scale: 0.8,  icon: '🦆' },
  { name: 'Quackie Chan',        chaosThreshold: 100, modelPath: '/models/ducky.glb',          scale: 1.0,  icon: '🥋🦆' },
  { name: 'The Quackfather',     chaosThreshold: 200, modelPath: '/models/mallard.glb',        scale: 1.3,  icon: '🎩🦆' },
  { name: 'Notorious D.U.C.K.',  chaosThreshold: 350, modelPath: '/models/duck-notorious.glb', scale: 1.6,  icon: '😎🦆' },
  { name: 'Duck Norris',         chaosThreshold: 500, modelPath: '/models/duck-with-gun.glb',  scale: 2.0,  icon: '🔫🦆' },
];

// ── Model loading ─────────────────────────────────────────────────────────

const loader = new GLTFLoader();
const loadedModels = new Map<string, THREE.Group>();
let preloaded = false;

/** Kick off async loading of every tier's GLB. Idempotent. */
export function preloadDuckModels(): void {
  if (preloaded) return;
  preloaded = true;
  for (const tier of DUCK_TIERS) {
    if (!tier.modelPath) continue;
    loader.load(tier.modelPath, (gltf) => {
      const model = gltf.scene;
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          mesh.castShadow = true;
          // Apply toon material for consistent look with the procedural duck.
          if (mesh.material) {
            const oldMat = mesh.material as THREE.MeshStandardMaterial;
            mesh.material = new THREE.MeshToonMaterial({
              color: oldMat.color ?? 0xffd700,
              map: oldMat.map ?? null,
            });
          }
        }
      });
      loadedModels.set(tier.modelPath!, model);
    });
  }
}

/** Returns the cached GLB for a given tier model path, or null if not loaded yet. */
export function getLoadedDuckModel(path: string): THREE.Group | null {
  return loadedModels.get(path) ?? null;
}
